import { Property } from '../types/user';
import { MonthlyStats } from '../types/property';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface PropertyReportProps {
  property: Property;
  monthlyStats: MonthlyStats[];
}

const COLORS = ['#ef4444', '#22c55e', '#3b82f6', '#f59e0b'];

export const PropertyReport = ({ property, monthlyStats = [] }: PropertyReportProps) => {
  // Calcular estadísticas desde monthlyStats en lugar de property.stats
  const totalRevenue = monthlyStats.reduce((sum, stat) => sum + (stat.revenue || 0), 0);
  const totalCleaningCosts = monthlyStats.reduce((sum, stat) => sum + (stat.cleaningCosts || 0), 0);
  const totalInmohostCosts = monthlyStats.reduce((sum, stat) => sum + (stat.inmohostCosts || 0), 0);
  const totalProfit = monthlyStats.reduce((sum, stat) => sum + (stat.profit || 0), 0);
  const totalReservations = monthlyStats.reduce((sum, stat) => sum + (stat.reservations || 0), 0);
  const totalOccupancy = monthlyStats.reduce((sum, stat) => sum + (stat.occupancyRate || 0), 0);
  const avgOccupancy = monthlyStats.length > 0 ? Math.round(totalOccupancy / monthlyStats.length) : 0;
  
  // Calcular precio promedio por noche
  const totalNightPrice = monthlyStats.reduce((sum, stat) => sum + (stat.averageNightPrice || 0), 0);
  const avgNightPrice = monthlyStats.length > 0 ? Math.round(totalNightPrice / monthlyStats.length) : 0;
  
  // Calcular estancia promedio
  const totalStayDuration = monthlyStats.reduce((sum, stat) => sum + (stat.averageStayDuration || 0), 0);
  const avgStayDuration = monthlyStats.length > 0 ? Math.round(totalStayDuration / monthlyStats.length) : 0;
  
  const stats = [
    {
      title: 'FACTURACIÓN',
      value: `${totalRevenue.toLocaleString()}€`,
      icon: (
        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
          <svg className="w-8 h-8 text-red-500" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z" />
          </svg>
        </div>
      )
    },
    {
      title: 'GASTOS LIMPIEZA',
      value: `${totalCleaningCosts.toLocaleString()}€`,
      icon: (
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
          <svg className="w-8 h-8 text-green-500" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19.36 2.72l1.42 1.42-5.72 5.71c1.07 1.54 1.22 3.39.32 4.59L9.06 8.12c1.2-.9 3.05-.75 4.59.32l5.71-5.72zM5.93 17.57c-2.01-2.01-3.24-4.41-3.58-6.65l4.88-2.09 7.73 7.73-2.09 4.88c-2.24-.34-4.64-1.57-6.65-3.58l-.29-.29z" />
          </svg>
        </div>
      )
    },
    {
      title: 'GASTOS INMOHOST',
      value: `${totalInmohostCosts.toLocaleString()}€`,
      icon: (
        <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
          <svg className="w-8 h-8 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
          </svg>
        </div>
      )
    },
    {
      title: 'BENEFICIO',
      value: `${totalProfit.toLocaleString()}€`,
      icon: (
        <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center">
          <svg className="w-8 h-8 text-purple-500" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11z" />
          </svg>
        </div>
      )
    },
    {
      title: 'OCUPACIÓN',
      value: `${avgOccupancy}%`,
      icon: (
        <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center">
          <svg className="w-8 h-8 text-yellow-500" viewBox="0 0 24 24" fill="currentColor">
            <path d="M7 13c1.66 0 3-1.34 3-3S8.66 7 7 7s-3 1.34-3 3 1.34 3 3 3zm12-6h-8v7H3V7H1v10h2v-3h18v3h2V9c0-2.21-1.79-4-4-4z" />
          </svg>
        </div>
      )
    },
    {
      title: 'RESERVAS',
      value: totalReservations.toString(),
      icon: (
        <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center">
          <svg className="w-8 h-8 text-indigo-500" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1z" />
          </svg>
        </div>
      )
    },
    {
      title: 'PRECIO/NOCHE',
      value: `${avgNightPrice.toLocaleString()}€`,
      icon: (
        <div className="w-16 h-16 rounded-full bg-pink-100 flex items-center justify-center">
          <svg className="w-8 h-8 text-pink-500" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.31-8.86c-1.77-.45-2.34-.94-2.34-1.67 0-.84.79-1.43 2.1-1.43 1.38 0 1.9.66 1.94 1.64h1.71c-.05-1.34-.87-2.57-2.49-2.97V5H10.9v1.69c-1.51.32-2.72 1.3-2.72 2.81 0 1.79 1.49 2.69 3.66 3.21 1.95.46 2.34 1.15 2.34 1.87 0 .53-.39 1.39-2.1 1.39-1.6 0-2.23-.72-2.32-1.64H8.04c.1 1.7 1.36 2.66 2.86 2.97V19h2.34v-1.67c1.52-.29 2.72-1.16 2.73-2.77-.01-2.2-1.9-2.96-3.66-3.42z" />
          </svg>
        </div>
      )
    },
    {
      title: 'ESTANCIA MEDIA',
      value: `${avgStayDuration} noches`,
      icon: (
        <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center">
          <svg className="w-8 h-8 text-teal-500" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.2 3.2.8-1.3-4.5-2.7V7z" />
          </svg>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">{property.name}</h2>
            <p className="text-xl text-gray-600">{property.location}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white rounded-lg p-6 text-center shadow-lg transform hover:scale-105 transition-transform duration-200">
              <div className="flex justify-center">{stat.icon}</div>
              <div className="text-2xl font-bold mb-2">{stat.value}</div>
              <div className="text-sm font-medium text-gray-500">{stat.title}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-xl font-semibold mb-6">Evolución de Ingresos y Beneficios</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyStats.map(stat => ({
                name: format(new Date(stat.year, stat.month - 1), 'MMM yyyy', { locale: es }),
                ingresos: stat.revenue,
                beneficio: stat.profit
              }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="ingresos" name="Ingresos" fill="#ef4444" />
                <Bar dataKey="beneficio" name="Beneficio" fill="#22c55e" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-xl font-semibold mb-6">Distribución de Ingresos</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={[
                    { name: 'Beneficio', value: property.stats.profit },
                    { name: 'Gastos Limpieza', value: property.stats.cleaningCosts },
                    { name: 'Gastos INMOHOST', value: property.stats.inmohostCosts }
                  ]}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {COLORS.map((color, index) => (
                    <Cell key={`cell-${index}`} fill={color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};